import React, { useState } from 'react';
import { formatMWK } from './tokens';
import { createPayout, getPayoutStatus } from '../../../services/pawapayService';
import { useCountry } from '../../../contexts/CountryContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DOTS = ['bg-red-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500'];

const CashoutModal = ({ driverId, balance, defaultSchedule = 'instant', onClose, onSaved }) => {
  const ctx = useCountry();
  const PROVIDERS = (ctx?.providers || []).map((p, i) => ({ id: p.id, label: p.label, dot: DOTS[i % DOTS.length] }));
  const [amount, setAmount] = useState(String(Math.floor(balance || 0)));
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [method, setMethod] = useState(PROVIDERS[0]?.id || '');
  const [phone, setPhone] = useState('');
  const [saveDefault, setSaveDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [statusText, setStatusText] = useState('');
  const [done, setDone] = useState(false);

  // Prefill from the driver's saved payout preference
  React.useEffect(() => {
    if (!driverId) return;
    fetch(`${API}/api/driver/v2/${driverId}/payout-preference`)
      .then(r => r.json())
      .then(d => {
        const p = d?.preference;
        if (p) {
          if (p.phone) setPhone(p.phone);
          if (p.provider) setMethod(p.provider);
        }
      })
      .catch(() => { /* ignore */ });
  }, [driverId]);

  const persistPreference = async () => {
    try {
      await fetch(`${API}/api/driver/v2/${driverId}/payout-preference`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, provider: method }),
      });
    } catch (_) { /* best-effort */ }
  };

  const runInstantPayout = async (n) => {
    setStatusText('Sending payout to your mobile money…');
    const po = await createPayout({
      amount: n,
      phoneNumber: phone,
      provider: method,
      country: ctx?.detectedIso3,
      driverId,
      description: 'Driver instant cash-out',
    });
    // Poll payout status
    let final = po.status;
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      const res = await getPayoutStatus(po.payoutId).catch(() => ({}));
      if (res.status) { final = res.status; }
      setStatusText(final === 'COMPLETED' ? 'Payout completed!' : 'Confirming payout…');
      if (['COMPLETED', 'FAILED', 'IN_RECONCILIATION'].includes(final)) break;
    }
    if (final === 'COMPLETED') {
      // Record the disbursement against the driver balance (best-effort)
      try {
        await fetch(`${API}/api/driver/v2/${driverId}/cashout-request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: n, schedule: 'instant', phone, payout_id: po.payoutId }),
        });
      } catch (_) { /* payout already sent; ledger sync is best-effort */ }
      return 'COMPLETED';
    }
    if (final === 'FAILED') throw new Error('The payout failed. Please check the number and try again.');
    if (final === 'IN_RECONCILIATION') return 'IN_RECONCILIATION';
    throw new Error('Payout is taking longer than expected. Please check again shortly.');
  };

  const runWeeklySchedule = async (n) => {
    const r = await fetch(`${API}/api/driver/v2/${driverId}/cashout-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: n, schedule, phone: phone || null }),
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      throw new Error(e.detail || 'Request failed');
    }
  };

  const handleSubmit = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) { setError('Enter a valid amount'); return; }
    if (n > balance) { setError(`Max available is ${formatMWK(balance)}`); return; }
    if (schedule === 'instant' && (!phone || phone.replace(/\D/g, '').length < 9)) {
      setError('Enter the mobile money number to receive the payout'); return;
    }
    setError('');
    setSubmitting(true);
    try {
      if (saveDefault) await persistPreference();
      if (schedule === 'instant') {
        const result = await runInstantPayout(n);
        setDone(true);
        setStatusText(result === 'COMPLETED' ? 'Money is on its way to your phone 🎉' : 'Payout submitted — confirming shortly.');
        setTimeout(() => onSaved?.(), 1600);
      } else {
        await runWeeklySchedule(n);
        onSaved?.();
      }
    } catch (e) {
      setError(String(e.message || e));
      setStatusText('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="cashout-modal"
      >
        <h3 className="text-lg font-semibold text-gray-900">Cash out</h3>
        <p className="text-xs text-gray-500 mt-0.5 mb-4">
          Available balance: <span className="font-semibold text-gray-900 tabular-nums">{formatMWK(balance)}</span>
        </p>

        {/* Schedule */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {[
            { id: 'instant', label: 'Instant', sub: 'To Airtel/TNM · No fee' },
            { id: 'weekly', label: 'Weekly', sub: 'Every Monday' },
          ].map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setSchedule(opt.id)}
              data-testid={`cashout-schedule-${opt.id}`}
              className={`p-3 rounded-2xl border-2 text-left transition-colors ${
                schedule === opt.id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-emerald-200'
              }`}
            >
              <p className="text-[13px] font-semibold text-gray-900">{opt.label}</p>
              <p className="text-[10px] text-gray-500">{opt.sub}</p>
            </button>
          ))}
        </div>

        {/* Provider (instant only) */}
        {schedule === 'instant' && (
          <>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Send to</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {PROVIDERS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setMethod(p.id)}
                  data-testid={`cashout-provider-${p.id}`}
                  className={`flex items-center gap-2 border-2 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors ${
                    method === p.id ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${p.dot}`} /> {p.label}
                </button>
              ))}
            </div>
          </>
        )}

        <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Amount</label>
        <input
          type="number"
          min={0}
          step={500}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="cashout-amount"
          className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-base tabular-nums"
        />

        <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1 mt-3">
          Mobile money phone {schedule === 'instant' ? '' : '(optional)'}
        </label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="0888..."
          data-testid="cashout-phone"
          className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
        />

        {error && <p className="text-[11px] text-rose-600 mt-2" data-testid="cashout-error">{error}</p>}
        {statusText && <p className="text-[12px] text-emerald-700 mt-2 font-medium" data-testid="cashout-status">{statusText}</p>}

        <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={saveDefault}
            onChange={(e) => setSaveDefault(e.target.checked)}
            data-testid="cashout-save-default"
            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-[12px] text-gray-600">Save as my default payout number &amp; provider</span>
        </label>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="flex-1 h-12 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm">
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="cashout-submit"
              className="flex-1 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold text-sm"
            >
              {submitting ? 'Processing…' : schedule === 'instant' ? `Withdraw ${formatMWK(amount)}` : `Request ${formatMWK(amount)}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashoutModal;
